/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/* eslint-disable sort-keys */

import {Sandbox} from '@exc/core';

const baseConfigs = {
  appId: 'target',
  base: '/target',
  distributionList: ['TargetEng@adobe.com'],
  serviceCode: 'dma_bullseye',
  access: ['dma_tnt', 'dma_bullseye'],
  analytics: {code: 'at'}
};

const newUiEnabledPages = new Set();
newUiEnabledPages.add('setup');
newUiEnabledPages.add('audiences');

const pagesInDevelopment = new Set(newUiEnabledPages);
pagesInDevelopment.add('activities');

const landingPath = '/apps/mac/target/pages/landing.html';
const discoveryEndpoint = '/bin/rapi/discovery';

const historyConfig = destination => {
  const config = {
    hideInitialSpinner: true,
    history: {
      type: Sandbox.HISTORY_TYPES.HASH,
      config: {hashType: 'noslash'}
    },
    sources: {
      dev: `http://localhost.corp.adobe.com:4502${landingPath}?dest=${destination}`,
      dev_old: `http://localhost.corp.adobe.com:4502${landingPath}?dest=${destination}`,
      dev_next: `http://localhost.corp.adobe.com:4502${landingPath}?dest=${destination}`,
      qa12: `https://{tenant}.marketing-qa12.corp.adobe.com${landingPath}?dest=${destination}`,
      qa12_old: `https://{tenant}.marketing-qa12.corp.adobe.com${landingPath}?dest=${destination}`,
      qa12_next: `https://{tenant}.marketing-qa12.corp.adobe.com${landingPath}?dest=${destination}`,
      qa: `https://{tenant}.experience-qa.corp.adobe.com${landingPath}?dest=${destination}`,
      qa_old: `https://{tenant}.experience-qa.corp.adobe.com${landingPath}?dest=${destination}`,
      qa_next: `https://{tenant}.experience-qa.corp.adobe.com${landingPath}?dest=${destination}`,
      beta: `https://{tenant}.experiencecloud-beta.adobe.com${landingPath}?dest=${destination}`,
      stage: `https://{tenant}.experiencecloud-stage.adobe.com${landingPath}?dest=${destination}`,
      stage_old: `https://{tenant}.experiencecloud-stage.adobe.com${landingPath}?dest=${destination}`,
      stage_next: `https://{tenant}.experiencecloud-stage.adobe.com${landingPath}?dest=${destination}`,
      prod: `https://{tenant}.experiencecloud.adobe.com${landingPath}?dest=${destination}`,
      prod_old: `https://{tenant}.experiencecloud.adobe.com${landingPath}?dest=${destination}`,
      prod_next: `https://{tenant}.experiencecloud.adobe.com${landingPath}?dest=${destination}`
    }
  };
  if (newUiEnabledPages.has(destination)) {
    config.sources.dev = {
      discovery: `http://localhost.corp.adobe.com:4502${discoveryEndpoint}?dest=${destination}`
    };
    config.sources.qa = {
      discovery: `https://{tenant}.experience-qa.corp.adobe.com${discoveryEndpoint}?dest=${destination}`
    };
    config.sources.qa12 = {
      discovery: `https://{tenant}.marketing-qa12.corp.adobe.com${discoveryEndpoint}?dest=${destination}`
    };
    config.sources.stage = {
      discovery: `https://{tenant}.experiencecloud-stage.adobe.com${discoveryEndpoint}?dest=${destination}`
    };
    config.sources.prod = {
      discovery: `https://{tenant}.experiencecloud.adobe.com${discoveryEndpoint}?dest=${destination}`
    };
  }
  if (pagesInDevelopment.has(destination)) {
    config.sources.dev_next = {
      discovery: `http://localhost.corp.adobe.com:4502${discoveryEndpoint}?dest=${destination}`
    };
    config.sources.qa_next = {
      discovery: `https://{tenant}.experience-qa.corp.adobe.com${discoveryEndpoint}?dest=${destination}`
    };
    config.sources.qa12_next = {
      discovery: `https://{tenant}.marketing-qa12.corp.adobe.com${discoveryEndpoint}?dest=${destination}`
    };
    config.sources.stage_next = {
      discovery: `https://{tenant}.experiencecloud-stage.adobe.com${discoveryEndpoint}?dest=${destination}`
    };
    config.sources.prod_next = {
      discovery: `https://{tenant}.experiencecloud.adobe.com${discoveryEndpoint}?dest=${destination}`
    };
  }
  return config;
};

export const targetActivities = {
  ...baseConfigs,
  name: 'Target Activities',
  path: '/target/activities',
  redirectFrom: ['/target'],
  sandbox: historyConfig('activities')
};

export const targetAudiences = {
  ...baseConfigs,
  name: 'Target Audiences',
  path: '/target/audiences',
  sandbox: historyConfig('audiences')
};

export const targetOffers = {
  ...baseConfigs,
  name: 'Target Offers',
  path: '/target/offers',
  sandbox: historyConfig('offers')
};

export const targetProducts = {
  ...baseConfigs,
  name: 'Target Recommendations',
  path: '/target/products',
  sandbox: historyConfig('products')
};

export const targetSetup = {
  ...baseConfigs,
  name: 'Target Setup',
  path: '/target/setup',
  sandbox: historyConfig('setup')
};
